import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Users, TrendingUp, Settings } from "lucide-react";
import { Button, Tabs, Row, Col } from "antd";
import useAuthStore from "stores/AuthStore/AuthStore";
import {
  getCommunity,
  getPortalCommunityStatistics,
  updateCommunity,
} from "services/communities.service";
import {
  renderErrorNotifications,
  renderSuccessNotifications,
} from "helpers/error.helpers";
import withBackButton from "hoc/withBackButton";
import useCommunityMasterData from "hooks/useCommunityMasterData/useCommunityMasterData";
import usePageTitle from "hooks/usePageTitle/usePageTitle";
import PageLoader from "Components/PageLoader/PageLoader";
import OverviewTab from "./_blocks/OverviewTab";
import MembersTab from "./_blocks/MembersTab";
import SettingsTab from "./_blocks/SettingsTab";

const TAB_ITEMS = ({
  community,
  communityStats,
  statsFailed,
  reloadStats,
  masterData,
  setActiveTab,
  handleSaveSettings,
  handleArchive,
}) => [
  {
    key: "overview",
    label: (
      <span className="flex items-center gap-2">
        <TrendingUp className="h-4 w-4" /> Overview
      </span>
    ),
    children: (
      <OverviewTab
        community={community}
        stats={communityStats}
        statsFailed={statsFailed}
        onRetryStats={reloadStats}
        setActiveTab={setActiveTab}
      />
    ),
  },
  {
    key: "members",
    label: (
      <span className="flex items-center gap-2">
        <Users className="h-4 w-4" /> People
      </span>
    ),
    children: <MembersTab />,
  },
  {
    key: "settings",
    label: (
      <span className="flex items-center gap-2">
        <Settings className="h-4 w-4" /> Settings
      </span>
    ),
    children: (
      <SettingsTab
        community={community}
        masterData={masterData}
        onSave={handleSaveSettings}
        onArchive={handleArchive}
      />
    ),
  },
];

function CommunityDetails() {
  const { communityId } = useParams();
  const { userData } = useAuthStore();
  const navigate = useNavigate();
  const masterData = useCommunityMasterData();

  const [community, setCommunity] = useState(null);
  const [communityStats, setCommunityStats] = useState(null);
  const [statsFailed, setStatsFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  usePageTitle(community?.name);

  const loadCommunityData = useCallback(
    async ({ showLoading = false } = {}) => {
      if (!communityId) return;
      if (showLoading) setLoading(true);
      const [
        { data: record, errors: recordErrors },
        { data: stats, errors: statsErrors },
      ] = await Promise.all([
        getCommunity(communityId),
        getPortalCommunityStatistics(communityId),
      ]);
      if (recordErrors?.length > 0 || !record) {
        renderErrorNotifications(
          recordErrors?.length > 0
            ? recordErrors
            : [{ message: "Community not found." }]
        );
        navigate("/shoup-community/communities");
        setLoading(false);
        return;
      }
      setCommunity(record);
      setCommunityStats(stats);
      setStatsFailed(statsErrors?.length > 0 || !stats);
      setLoading(false);
    },
    [communityId, navigate]
  );

  const reloadStats = async () => {
    const { data: stats, errors: statsErrors } =
      await getPortalCommunityStatistics(communityId);
    setCommunityStats(stats);
    setStatsFailed(statsErrors?.length > 0 || !stats);
  };

  useEffect(() => {
    loadCommunityData({ showLoading: true });
  }, [loadCommunityData]);

  const handleSaveSettings = async (formValues) => {
    if (!communityId) return false;
    const { errors: updateErrors } = await updateCommunity(communityId, {
      ...formValues,
      audit: { updated_by: userData.email },
    });
    if (updateErrors?.length > 0) {
      renderErrorNotifications(updateErrors);
      return false;
    }
    await loadCommunityData();
    renderSuccessNotifications({
      title: "Community updated",
      message: "Your changes have been saved.",
    });
    return true;
  };

  const handleArchive = async (isActive) => {
    const activating = isActive === true;
    if (
      !communityId ||
      !confirm(
        activating
          ? "Mark this community as active?"
          : "Mark this community as inactive? You can re-activate later if needed."
      )
    ) {
      return;
    }
    const { errors: archiveErrors } = await updateCommunity(communityId, {
      is_active: activating,
      audit: { updated_by: userData.email },
    });
    if (archiveErrors?.length > 0) {
      renderErrorNotifications(archiveErrors);
      return;
    }
    await loadCommunityData();
    renderSuccessNotifications({
      title: activating ? "Community activated" : "Community inactive",
      message: activating
        ? "The community has been marked as active."
        : "The community has been marked as inactive.",
    });
  };

  if (loading)
    return <PageLoader showCards={false} showTabs={false} showHero={false} />;

  if (!community) {
    return (
      <div className="space-y-4 text-center py-20">
        <p className="text-lg font-semibold">Community not found</p>
        <Button type="default">
          <Link to="/shoup-community/communities">Back to Communities</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-border/50 bg-card overflow-hidden shadow-card">
        <div
          className={`h-36 relative ${community.cover_url ? "" : "bg-gradient-shoup"}`}
          style={
            community.cover_url
              ? {
                  backgroundImage: `url(${community.cover_url})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }
              : undefined
          }
        >
          <div className="absolute inset-0 bg-foreground/35" aria-hidden />
          <div className="absolute bottom-4 left-6">
            <Row align="middle" gutter={16} wrap={false}>
              <Col flex="none">
                <div className="h-14 w-14 rounded-2xl bg-background flex items-center justify-center shadow-card border border-border/60 overflow-hidden">
                  {community.logo_url ? (
                    <img
                      src={community.logo_url}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Users className="h-7 w-7 text-foreground/80" />
                  )}
                </div>
              </Col>
              <Col flex="auto">
                <div className="space-y-1 text-white drop-shadow-sm">
                  <h1 className="text-xl font-bold tracking-tight">
                    {community.name}
                  </h1>
                  <p className="text-xs text-white/90 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span>
                      {(community.activities ?? []).map((a) => a.name).join(", ")}
                    </span>
                    <span aria-hidden>·</span>
                    <span>{community.verification_status}</span>
                  </p>
                </div>
              </Col>
            </Row>
          </div>
        </div>

        <div className="px-4 md:px-6">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={TAB_ITEMS({
              community,
              communityStats,
              statsFailed,
              reloadStats,
              masterData,
              setActiveTab,
              handleSaveSettings,
              handleArchive,
            })}
          />
        </div>
      </div>
    </div>
  );
}

export default withBackButton(CommunityDetails);
